// Minimum height for an autoResize textarea. Default 24px matches every existing
// usage; opt into a smaller floor with `data-min-height="18px"` on the element
// when a denser cell needs to take less vertical space.
const floorFor = (el) => el.dataset?.minHeight || '24px';

// A textarea inside a display:none ancestor (a collapsed group, a v-show tab) measures
// scrollHeight 0. Measuring then pins it at 0px, and nothing re-measures once it is shown.
const isHidden = (el) => el.getClientRects().length === 0;

const fitToContent = (el) => {
  if (isHidden(el)) return;
  el.style.overflowY = 'hidden';
  el.style.height = floorFor(el);
  el.style.height = el.scrollHeight + 'px';
  el.style.overflowY = el.scrollHeight > el.offsetHeight ? 'auto' : 'hidden';
};

export default {
    mounted(el) {
      const autoResize = () => {
        if (isHidden(el)) return;
        const placeholder = el.placeholder;
        el.placeholder = '';
        el.style.overflowY = 'hidden';
        // Collapse to the floor first, then adopt scrollHeight. scrollHeight can never
        // report less than the visible box, so this yields max(floor, content height) —
        // the floor still holds, and an empty field gets one natural line instead of
        // being squashed below its own line-height (which left it clipped, with a
        // scrollbar, until the first `updated` pass expanded it).
        el.style.height = floorFor(el);
        el.style.height = el.scrollHeight + 'px';
        el.placeholder = placeholder;
        el.style.overflowY = el.scrollHeight > el.offsetHeight ? 'auto' : 'hidden';
      };

      const handleKeydown = (event) => {
        if (el.hasAttribute('data-disallow-enter') && event.key === 'Enter') {
          event.preventDefault();
          el.blur();
        }
      };

      // Initial resize
      autoResize();

      // Add event listeners
      el.addEventListener('input', autoResize);
      el.addEventListener('paste', autoResize);
      el.addEventListener('keydown', handleKeydown);

      // Re-measure whenever the textarea comes into view — covers mounting hidden and being shown later.
      if (typeof IntersectionObserver !== 'undefined') {
        el._autoResizeObserver = new IntersectionObserver((entries) => {
          if (entries.some(entry => entry.isIntersecting)) autoResize();
        });
        el._autoResizeObserver.observe(el);
      }
    },
    updated(el) {
      fitToContent(el);
    },
    unmounted(el) {
      el._autoResizeObserver?.disconnect();
      el._autoResizeObserver = null;
    },
};
